import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import useFetchQuestions from "../hooks/useFetchQuestions"
import useIsMobile from "../hooks/useIsMobile"

const OPTION_KEYS = ["option_a", "option_b", "option_c", "option_d"]
const OPTION_LABELS = ["A", "B", "C", "D"]

function postResults(scriptUrl, quizId, answers, questions, contact) {
    if (!scriptUrl) return
    const score = answers.filter((a, i) => a === questions[i].correct).length
    fetch(scriptUrl, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            quizId,
            name: contact?.name ?? "",
            email: contact?.email ?? "",
            score,
            total: questions.length,
            timestamp: new Date().toISOString(),
            answers: answers.map((selected, i) => ({
                questionId: questions[i].id,
                selected,
                correct: questions[i].correct,
                isCorrect: selected === questions[i].correct,
            })),
        }),
    }).catch(() => {})
}

function postFeedback(scriptUrl, quizId, rating, comment) {
    if (!scriptUrl) return
    const url = scriptUrl
        + "?action=feedback"
        + "&quiz_id=" + encodeURIComponent(quizId)
        + "&rating="  + encodeURIComponent(rating)
        + "&comment=" + encodeURIComponent(comment || "")
    fetch(url).catch(() => {})
}

function submitEmailReport(scriptUrl, quizId, score, total, email) {
    if (!scriptUrl || !email) return
    const url = scriptUrl
        + "?action=email_report"
        + "&email="   + encodeURIComponent(email)
        + "&quiz_id=" + encodeURIComponent(quizId)
        + "&score="   + encodeURIComponent(score)
        + "&total="   + encodeURIComponent(total)
    fetch(url).catch(() => {})
}

async function validateCode(scriptUrl, code, quizId) {
    const url = `${scriptUrl}?action=validate&code=${encodeURIComponent(code)}&quizId=${encodeURIComponent(quizId)}`
    const res = await fetch(url)
    const data = await res.json()
    return data.valid === true
}

function detectQuestionType(questionText) {
    const lower = (questionText || "").toLowerCase()
    const appliedKeywords = ["assign", "code for", "which code", "a patient", "the physician", "procedure", "report", "select the correct", "documentation", "icd-10", "cpt", "hcpcs", "diagnosis"]
    return appliedKeywords.some(function(k) { return lower.indexOf(k) !== -1 }) ? "applied" : "conceptual"
}

function buildProfile(questions, answers, quizId) {
    var results = questions.map(function(q, i) {
        return { correct: answers[i] === q.correct, type: detectQuestionType(q.question) }
    })
    var score          = results.filter(function(r) { return r.correct }).length
    var total          = results.length
    var wrong          = results.filter(function(r) { return !r.correct })
    var applied        = results.filter(function(r) { return r.type === "applied" })
    var conceptual     = results.filter(function(r) { return r.type === "conceptual" })
    var appliedRight   = applied.filter(function(r) { return r.correct }).length
    var conceptualRight = conceptual.filter(function(r) { return r.correct }).length
    var appliedWrong   = applied.length - appliedRight
    var conceptualWrong = conceptual.length - conceptualRight

    var topicLabel = quizId === "free-icd10" ? "diagnosis coding"
                   : quizId === "free-em"    ? "E&M documentation"
                   :                           "medical terminology"

    var insights = []

    // Strength
    if (score === total) {
        insights.push({ kind: "strength", text: "Perfect score on this sample — your " + topicLabel + " fundamentals are solid. The full 35-question diagnostic tests deeper concepts and edge cases you'll see on exam day." })
    } else if (applied.length > 1 && appliedRight === applied.length) {
        insights.push({ kind: "strength", text: "Strong on application — you can translate rules into correct answers in clinical scenarios, which is exactly how the CPC exam is structured." })
    } else if (conceptual.length > 1 && conceptualRight === conceptual.length) {
        insights.push({ kind: "strength", text: "Your conceptual knowledge of " + topicLabel + " is intact — you understand the definitions and underlying rules." })
    } else if (score > 0) {
        insights.push({ kind: "strength", text: "You answered " + score + " of " + total + " correctly — there's a foundation here, but some key concepts aren't fully locking in yet." })
    }

    // Gap
    if (wrong.length > 0) {
        if (appliedWrong > 0 && appliedWrong >= conceptualWrong) {
            insights.push({ kind: "gap", text: "Application gap detected — you know the rules in isolation but struggle when they appear inside a patient scenario. This is one of the most common reasons CPC candidates miss passing by a narrow margin." })
        } else if (conceptualWrong > 0) {
            insights.push({ kind: "gap", text: "Conceptual gaps in " + topicLabel + " — some core definitions and guidelines aren't fully locked in. The CPC exam builds multi-step questions on top of these foundations." })
        }
    }

    // Next step
    if (score <= 2) {
        insights.push({ kind: "next", text: "The full 35-question diagnostic targets every gap in this sample — with explanations tied to official coding guidelines so you learn the rule, not just the answer." })
    } else if (score <= 4) {
        insights.push({ kind: "next", text: "The full 35-question diagnostic surfaces the edge cases this 5-question sample doesn't reach — the difference between a narrow pass and a miss on exam day." })
    } else {
        insights.push({ kind: "next", text: "Even with a perfect score here, the full diagnostic covers concepts this 5-question sample can't reach. Verify your readiness before exam day." })
    }

    return insights
}

function getInitialPhase(quiz) {
    if (quiz.isPaid) {
        const unlocked = localStorage.getItem(`qwiz_unlocked_${quiz.id}`)
        if (!unlocked) return "gate"
    }
    return quiz.collectContact ? "intake" : "quiz"
}

export default function Quiz({ quiz }) {
    const isMobile = useIsMobile()
    const { questions, loading, error } = useFetchQuestions(
        quiz.scriptUrl, quiz.id, quiz.sampleQuestions ?? []
    )
    const [phase, setPhase] = useState(() => getInitialPhase(quiz))
    const [contact, setContact] = useState({ name: "", email: "" })
    const [codeInput, setCodeInput] = useState("")
    const [codeError, setCodeError] = useState(null)
    const [validating, setValidating] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [answers, setAnswers] = useState([])
    const [selected, setSelected] = useState(null)
    const [feedbackRating, setFeedbackRating] = useState(null)
    const [feedbackComment, setFeedbackComment] = useState("")
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
    const [reportEmail, setReportEmail] = useState("")
    const [reportSent, setReportSent] = useState(false)
    const [reportLoading, setReportLoading] = useState(false)
    const [reportSkipped, setReportSkipped] = useState(false)

    useEffect(() => {
        if (phase === "quiz" && typeof window.gtag === "function") {
            window.gtag("event", "quiz_started", {
                quiz_id:   quiz.id,
                quiz_type: quiz.isPaid ? "paid" : "free",
            })
        }
    }, [phase])

    const afterUnlock = () => {
        localStorage.setItem(`qwiz_unlocked_${quiz.id}`, "1")
        setPhase(quiz.collectContact ? "intake" : "quiz")
    }

    const handleValidate = async () => {
        if (!codeInput.trim()) return
        setValidating(true)
        setCodeError(null)
        const valid = await validateCode(quiz.scriptUrl, codeInput.trim(), quiz.id)
        setValidating(false)
        if (valid) {
            afterUnlock()
        } else {
            setCodeError("Invalid code. Please check and try again.")
        }
    }

    // ── Gate ─────────────────────────────────────────────────────────────────
    if (phase === "gate") {
        const inputStyle = {
            flex: 1, padding: "12px 14px", borderRadius: 10,
            border: "1.5px solid #E5E7EB", fontSize: 15, fontFamily: "inherit",
            color: "#111827", outline: "none", boxSizing: "border-box",
        }
        return (
            <div style={{ maxWidth: 480, margin: "0 auto" }}>
                <div style={{
                    border: "1px solid #E5E7EB", borderRadius: 20,
                    padding: isMobile ? "32px 24px" : "44px 48px",
                    boxShadow: "0 10px 30px rgba(17,24,39,0.05)",
                    textAlign: "center",
                }}>
                    {/* Lock icon */}
                    <div style={{
                        width: 56, height: 56, borderRadius: 16,
                        background: "#EFF6FF", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        margin: "0 auto 20px",
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                    </div>

                    <h2 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, margin: "0 0 8px", letterSpacing: "-0.03em" }}>
                        Premium Quiz
                    </h2>
                    <p style={{ fontSize: 15, color: "#6B7280", margin: "0 0 24px", lineHeight: 1.6 }}>
                        Full access to this diagnostic quiz with detailed explanations.
                    </p>

                    <div style={{ fontSize: isMobile ? 36 : 44, fontWeight: 800, letterSpacing: "-0.03em", color: "#111827", marginBottom: 4 }}>
                        {quiz.price}
                    </div>
                    <p style={{ fontSize: 13, color: "#9CA3AF", margin: "0 0 20px" }}>
                        CPC exam retake costs $399 — this is {quiz.price}.
                    </p>

                    {quiz.stripeUrl ? (
                        <>
                            <a
                                href={quiz.stripeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: "block", width: "100%", boxSizing: "border-box",
                                    background: "#2563EB", color: "white",
                                    padding: "14px 0", borderRadius: 12,
                                    textDecoration: "none", fontWeight: 700, fontSize: 16,
                                    marginBottom: 10,
                                }}
                            >
                                Buy on Stripe →
                            </a>
                            <p style={{ fontSize: 13, color: "#6B7280", textAlign: "center", margin: "0 0 20px", lineHeight: 1.6 }}>
                                Your access code arrives in your inbox within seconds.<br />
                                Enter it below to unlock — no account needed.<br />
                                <span style={{ color: "#9CA3AF" }}>Can't find it? Check your spam folder.</span>
                            </p>
                        </>
                    ) : (
                        <div style={{
                            background: "#F9FAFB", borderRadius: 12, padding: "14px",
                            fontSize: 14, color: "#9CA3AF", marginBottom: 28,
                        }}>
                            Payments coming soon — check back shortly.
                        </div>
                    )}

                    {/* Divider */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                        <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
                        <span style={{ fontSize: 13, color: "#9CA3AF", whiteSpace: "nowrap" }}>Already purchased?</span>
                        <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
                    </div>

                    {/* Code entry */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <input
                            type="text"
                            placeholder="Enter access code"
                            value={codeInput}
                            onChange={e => { setCodeInput(e.target.value); setCodeError(null) }}
                            onKeyDown={e => e.key === "Enter" && handleValidate()}
                            style={inputStyle}
                        />
                        <button
                            onClick={handleValidate}
                            disabled={!codeInput.trim() || validating}
                            style={{
                                background: codeInput.trim() && !validating ? "#111827" : "#E5E7EB",
                                color: codeInput.trim() && !validating ? "white" : "#9CA3AF",
                                padding: "12px 18px", borderRadius: 10,
                                border: "none", cursor: codeInput.trim() && !validating ? "pointer" : "not-allowed",
                                fontWeight: 700, fontSize: 15, fontFamily: "inherit",
                                whiteSpace: "nowrap",
                                transition: "background 0.15s, color 0.15s",
                            }}
                        >
                            {validating ? "Checking…" : "Unlock"}
                        </button>
                    </div>

                    {codeError && (
                        <p style={{ fontSize: 13, color: "#DC2626", margin: "6px 0 0", textAlign: "left" }}>
                            {codeError}
                        </p>
                    )}

                    <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "10px 14px", margin: "16px 0 0", display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <span style={{ color: "#16A34A", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>✓</span>
                        <p style={{ fontSize: 13, color: "#15803D", margin: 0, lineHeight: 1.6 }}>
                            <strong>7-day money-back guarantee.</strong> If the quiz isn't what you expected, email us at listener@qwiztime.ai and we'll refund you — no questions asked.
                        </p>
                    </div>
                    <p style={{ fontSize: 12, color: "#9CA3AF", margin: "12px 0 0", lineHeight: 1.6 }}>
                        This is an independent practice diagnostic. It does not guarantee exam results or certification.
                    </p>
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#6B7280", fontSize: 18 }}>
                Loading questions…
            </div>
        )
    }

    if (error || questions.length === 0) {
        return (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#6B7280", fontSize: 18 }}>
                {error ?? "No questions available yet. Check back soon."}
            </div>
        )
    }

    const total = questions.length
    const question = questions[currentIndex]
    const isLast = currentIndex === total - 1

    const handleNext = () => {
        const newAnswers = [...answers]
        newAnswers[currentIndex] = selected
        if (isLast) {
            postResults(quiz.scriptUrl, quiz.id, newAnswers, questions, contact)
            setAnswers(newAnswers)
            setPhase("results")
        } else {
            setAnswers(newAnswers)
            setCurrentIndex(currentIndex + 1)
            setSelected(newAnswers[currentIndex + 1] ?? null)
        }
    }

    const handlePrev = () => {
        const newAnswers = [...answers]
        if (selected) newAnswers[currentIndex] = selected
        setAnswers(newAnswers)
        setCurrentIndex(currentIndex - 1)
        setSelected(newAnswers[currentIndex - 1] ?? null)
    }

    // ── Intake ───────────────────────────────────────────────────────────────
    if (phase === "intake") {
        const canStart = contact.name.trim().length > 0 && contact.email.trim().length > 0
        const inputStyle = {
            width: "100%", padding: "12px 14px", borderRadius: 10,
            border: "1.5px solid #E5E7EB", fontSize: 15, fontFamily: "inherit",
            color: "#111827", outline: "none", boxSizing: "border-box",
            marginTop: 6,
        }
        const labelStyle = {
            display: "block", fontSize: 14, fontWeight: 600,
            color: "#374151", marginBottom: 16,
        }
        return (
            <div style={{ maxWidth: 480, margin: "0 auto" }}>
                <div style={{
                    border: "1px solid #E5E7EB", borderRadius: 20,
                    padding: isMobile ? "28px 24px" : "40px 48px",
                    boxShadow: "0 10px 30px rgba(17,24,39,0.05)",
                }}>
                    <h2 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, margin: "0 0 8px", letterSpacing: "-0.03em" }}>
                        Before we begin
                    </h2>
                    <p style={{ fontSize: 15, color: "#6B7280", margin: "0 0 28px", lineHeight: 1.6 }}>
                        Enter your details to get started.
                    </p>

                    <label style={labelStyle}>
                        Name
                        <input
                            type="text"
                            placeholder="Your name"
                            value={contact.name}
                            onChange={e => setContact(c => ({ ...c, name: e.target.value }))}
                            style={inputStyle}
                        />
                    </label>

                    <label style={labelStyle}>
                        Email
                        <input
                            type="email"
                            placeholder="you@example.com"
                            value={contact.email}
                            onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                            onKeyDown={e => e.key === "Enter" && canStart && setPhase("quiz")}
                            style={inputStyle}
                        />
                    </label>

                    <button
                        onClick={() => setPhase("quiz")}
                        disabled={!canStart}
                        style={{
                            width: "100%", marginTop: 8,
                            background: canStart ? "#2563EB" : "#E5E7EB",
                            color: canStart ? "white" : "#9CA3AF",
                            padding: "14px 0", borderRadius: 12,
                            border: "none", cursor: canStart ? "pointer" : "not-allowed",
                            fontWeight: 700, fontSize: 16, fontFamily: "inherit",
                            transition: "background 0.15s, color 0.15s",
                        }}
                    >
                        Start Quiz →
                    </button>

                    <p style={{ fontSize: 13, color: "#9CA3AF", textAlign: "center", margin: "16px 0 0" }}>
                        We don't share your information with third parties.
                    </p>
                    <p style={{ fontSize: 12, color: "#9CA3AF", textAlign: "center", margin: "10px 0 0", lineHeight: 1.6 }}>
                        This is an independent practice diagnostic. It does not guarantee exam results or certification.
                    </p>
                </div>
            </div>
        )
    }

    // ── Results ──────────────────────────────────────────────────────────────
    if (phase === "results") {
        const score = answers.filter((a, i) => a === questions[i].correct).length
        const pct = Math.round((score / total) * 100)
        const verdict = pct >= 80
            ? "Great work!"
            : pct >= 60
            ? "Good effort — a few gaps to review."
            : "Keep practicing — the explanations below will help."
        const scoreColor = pct >= 80 ? "#16A34A" : pct >= 60 ? "#CA8A04" : "#DC2626"

        const readinessMap = [10, 18, 36, 54, 72, 90]
        const readiness = !quiz.isPaid ? readinessMap[Math.min(score, readinessMap.length - 1)] : null
        const readinessColor = readiness >= 70 ? "#16A34A" : readiness >= 50 ? "#CA8A04" : "#DC2626"
        const readinessMessage = readiness >= 90
            ? "Strong foundation. The full 35-question diagnostic tests deeper concepts and edge cases you'll see on exam day."
            : readiness >= 70
            ? "Solid base, but gaps remain. The full diagnostic will surface what's missing before exam day."
            : readiness >= 50
            ? "At risk. Exam questions go deeper than this sample — this topic could cost you the pass."
            : "Significant gaps in this area. Don't let this topic be the reason you retake the exam."

        return (
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
                <div style={{
                    border: "1px solid #E5E7EB", borderRadius: 20,
                    padding: isMobile ? "28px 24px" : "40px 48px",
                    marginBottom: 24, textAlign: "center",
                    boxShadow: "0 10px 30px rgba(17,24,39,0.05)",
                }}>
                    <div style={{ fontSize: isMobile ? 52 : 68, fontWeight: 800, letterSpacing: "-0.04em", color: "#111827", lineHeight: 1 }}>
                        {score}
                        <span style={{ fontSize: isMobile ? 24 : 32, color: "#9CA3AF", fontWeight: 600 }}>/{total}</span>
                    </div>
                    <div style={{ fontSize: isMobile ? 15 : 17, color: "#6B7280", marginTop: 10 }}>
                        {verdict}
                    </div>
                    <div style={{ background: "#F3F4F6", borderRadius: 99, height: 8, marginTop: 20, overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: scoreColor, borderRadius: 99 }} />
                    </div>
                </div>

                {readiness !== null && (
                    <div style={{
                        border: `1.5px solid ${readinessColor}44`,
                        borderRadius: 20,
                        padding: isMobile ? "28px 24px" : "36px 48px",
                        marginBottom: 24,
                        background: `${readinessColor}07`,
                    }}>
                        {/* Header row */}
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                    <span style={{ background: "#EFF6FF", color: "#2563EB", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>
                                        ✦ ANALYSIS
                                    </span>
                                </div>
                                <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.02em" }}>
                                    Your CPC Readiness Profile
                                </div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0 }}>
                                <div style={{ fontSize: 12, color: readinessColor, fontWeight: 600, marginBottom: 2 }}>Readiness Score</div>
                                <div style={{ fontSize: isMobile ? 40 : 52, fontWeight: 800, color: readinessColor, lineHeight: 1, letterSpacing: "-0.04em" }}>
                                    {readiness}%
                                </div>
                            </div>
                        </div>

                        {/* Insights */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 28 }}>
                            {buildProfile(questions, answers, quiz.id).map(function(insight, i) {
                                var iconBg    = insight.kind === "strength" ? "#DCFCE7" : insight.kind === "gap" ? "#FEE2E2" : "#EFF6FF"
                                var iconColor = insight.kind === "strength" ? "#16A34A" : insight.kind === "gap" ? "#DC2626" : "#2563EB"
                                var icon      = insight.kind === "strength" ? "↑"       : insight.kind === "gap" ? "!"        : "→"
                                return (
                                    <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                                        <span style={{
                                            flexShrink: 0, width: 24, height: 24, borderRadius: 6,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: 12, fontWeight: 800,
                                            background: iconBg, color: iconColor,
                                        }}>
                                            {icon}
                                        </span>
                                        <p style={{ margin: 0, fontSize: isMobile ? 14 : 15, color: "#374151", lineHeight: 1.65 }}>
                                            {insight.text}
                                        </p>
                                    </div>
                                )
                            })}
                        </div>

                        {/* CTA */}
                        {quiz.upgradeUrl && (
                            <>
                                <a href={quiz.upgradeUrl} style={{
                                    display: "block", textAlign: "center",
                                    background: "#2563EB", color: "white",
                                    padding: isMobile ? "14px 24px" : "16px 32px",
                                    borderRadius: 12, textDecoration: "none",
                                    fontWeight: 700, fontSize: isMobile ? 15 : 16,
                                }}>
                                    Get the Full 35-Question Diagnostic — $9 →
                                </a>
                                <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 10, textAlign: "center" }}>
                                    Permanent access · Detailed explanations · 7-day money-back guarantee
                                </div>
                            </>
                        )}
                    </div>
                )}

                {!quiz.isPaid && (
                    <div style={{
                        border: "1px solid #E5E7EB", borderRadius: 20,
                        padding: isMobile ? "24px 20px" : "32px 40px",
                        marginBottom: 24,
                        background: "#FAFAFA",
                        textAlign: "center",
                    }}>
                        {reportSent ? (
                            <div>
                                <div style={{ fontSize: 28, marginBottom: 10 }}>✓</div>
                                <p style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: "#16A34A", margin: "0 0 6px" }}>
                                    Report sent!
                                </p>
                                <p style={{ fontSize: 14, color: "#6B7280", margin: 0 }}>
                                    Check your inbox — and your spam folder if you don't see it within a minute.
                                </p>
                            </div>
                        ) : reportSkipped ? null : (
                            <>
                                <p style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: "#111827", margin: "0 0 6px" }}>
                                    Want your results report by email?
                                </p>
                                <p style={{ fontSize: 14, color: "#6B7280", margin: "0 0 20px", lineHeight: 1.6 }}>
                                    We'll send your score, readiness breakdown, and what to focus on next. One email, no spam.
                                </p>
                                <div style={{ display: "flex", gap: 8, maxWidth: 400, margin: "0 auto 12px" }}>
                                    <input
                                        type="email"
                                        placeholder="your@email.com"
                                        value={reportEmail}
                                        onChange={e => setReportEmail(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter" && reportEmail.trim()) {
                                                submitEmailReport(quiz.scriptUrl, quiz.id, score, total, reportEmail.trim())
                                                setReportSent(true)
                                            }
                                        }}
                                        style={{
                                            flex: 1, padding: "11px 14px", borderRadius: 10,
                                            border: "1.5px solid #D1D5DB", fontSize: 15,
                                            fontFamily: "inherit", color: "#111827",
                                            outline: "none", boxSizing: "border-box",
                                        }}
                                    />
                                    <button
                                        onClick={() => {
                                            if (!reportEmail.trim()) return
                                            submitEmailReport(quiz.scriptUrl, quiz.id, score, total, reportEmail.trim())
                                            setReportSent(true)
                                        }}
                                        disabled={!reportEmail.trim() || reportLoading}
                                        style={{
                                            background: reportEmail.trim() ? "#2563EB" : "#E5E7EB",
                                            color: reportEmail.trim() ? "white" : "#9CA3AF",
                                            padding: "11px 18px", borderRadius: 10,
                                            border: "none", cursor: reportEmail.trim() ? "pointer" : "not-allowed",
                                            fontWeight: 700, fontSize: 14, fontFamily: "inherit",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        Send Report
                                    </button>
                                </div>
                                <button
                                    onClick={() => setReportSkipped(true)}
                                    style={{
                                        background: "none", border: "none", cursor: "pointer",
                                        fontSize: 13, color: "#9CA3AF", fontFamily: "inherit",
                                    }}
                                >
                                    No thanks, I'll review below
                                </button>
                            </>
                        )}
                    </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
                    {questions.map((q, i) => {
                        const userKey = answers[i]
                        const isCorrect = userKey === q.correct
                        const userLabel = OPTION_LABELS[OPTION_KEYS.indexOf(userKey)]
                        const correctLabel = OPTION_LABELS[OPTION_KEYS.indexOf(q.correct)]
                        return (
                            <div key={q.id ?? i} style={{
                                border: `1px solid ${isCorrect ? "#BBF7D0" : "#FECACA"}`,
                                borderRadius: 16,
                                padding: isMobile ? "18px 18px" : "22px 26px",
                                background: isCorrect ? "#F0FDF4" : "#FFF5F5",
                            }}>
                                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                                    <span style={{ fontSize: 15, fontWeight: 700, flexShrink: 0, marginTop: 1, color: isCorrect ? "#16A34A" : "#DC2626" }}>
                                        {isCorrect ? "✓" : "✗"}
                                    </span>
                                    <div style={{ flex: 1 }}>
                                        <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: isMobile ? 14 : 15, color: "#111827", lineHeight: 1.5 }}>
                                            Q{i + 1}: {q.question}
                                        </p>
                                        {isCorrect ? (
                                            <p style={{ margin: 0, fontSize: 14, color: "#16A34A" }}>
                                                {userLabel}. {q[userKey]}
                                            </p>
                                        ) : (
                                            <>
                                                <p style={{ margin: "0 0 3px", fontSize: 14, color: "#DC2626" }}>
                                                    Your answer: {userLabel}. {q[userKey]}
                                                </p>
                                                <p style={{ margin: 0, fontSize: 14, color: "#16A34A", fontWeight: 600 }}>
                                                    Correct: {correctLabel}. {q[q.correct]}
                                                </p>
                                            </>
                                        )}
                                        {q.explanation && (
                                            <p style={{ margin: "10px 0 0", fontSize: 13, color: "#6B7280", lineHeight: 1.65 }}>
                                                {q.explanation}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {!quiz.isPaid && (
                    <div style={{
                        border: "1px solid #E5E7EB", borderRadius: 20,
                        padding: isMobile ? "24px 20px" : "32px 40px",
                        marginBottom: 24, textAlign: "center",
                        boxShadow: "0 4px 16px rgba(17,24,39,0.04)",
                    }}>
                        {feedbackSubmitted ? (
                            <p style={{ fontSize: 16, fontWeight: 600, color: "#16A34A", margin: 0 }}>
                                Thanks for the feedback!
                            </p>
                        ) : (
                            <>
                                <p style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: "#111827", margin: "0 0 16px" }}>
                                    Was this quiz helpful?
                                </p>
                                <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 16 }}>
                                    {[{ val: "up", label: "👍 Yes" }, { val: "down", label: "👎 No" }].map(({ val, label }) => (
                                        <button
                                            key={val}
                                            onClick={() => setFeedbackRating(val)}
                                            style={{
                                                padding: "10px 24px", borderRadius: 10,
                                                border: feedbackRating === val ? "2px solid #2563EB" : "1.5px solid #E5E7EB",
                                                background: feedbackRating === val ? "#EFF6FF" : "white",
                                                fontFamily: "inherit", fontWeight: 600, fontSize: 15,
                                                cursor: "pointer", color: "#111827",
                                            }}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <textarea
                                    placeholder="Any comments? (optional)"
                                    value={feedbackComment}
                                    onChange={e => setFeedbackComment(e.target.value)}
                                    rows={2}
                                    style={{
                                        width: "100%", boxSizing: "border-box",
                                        padding: "10px 14px", borderRadius: 10,
                                        border: "1.5px solid #E5E7EB", fontSize: 14,
                                        fontFamily: "inherit", color: "#111827",
                                        resize: "vertical", marginBottom: 12, outline: "none",
                                    }}
                                />
                                <button
                                    onClick={() => {
                                        postFeedback(quiz.scriptUrl, quiz.id, feedbackRating, feedbackComment)
                                        setFeedbackSubmitted(true)
                                    }}
                                    disabled={!feedbackRating}
                                    style={{
                                        background: feedbackRating ? "#111827" : "#E5E7EB",
                                        color: feedbackRating ? "white" : "#9CA3AF",
                                        padding: "12px 28px", borderRadius: 10,
                                        border: "none", cursor: feedbackRating ? "pointer" : "not-allowed",
                                        fontWeight: 700, fontSize: 15, fontFamily: "inherit",
                                    }}
                                >
                                    Submit feedback
                                </button>
                            </>
                        )}
                    </div>
                )}

                <div style={{ textAlign: "center" }}>
                    <Link to="/" style={{
                        display: "inline-block",
                        background: "#2563EB", color: "white",
                        padding: "14px 28px", borderRadius: 12,
                        textDecoration: "none", fontWeight: 700, fontSize: 16,
                    }}>
                        Browse other quizzes
                    </Link>
                </div>
            </div>
        )
    }

    // ── Quiz ─────────────────────────────────────────────────────────────────
    const progressPct = (currentIndex / total) * 100

    return (
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div style={{ marginBottom: 28 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 14, color: "#6B7280", fontWeight: 500 }}>
                        Question {currentIndex + 1} of {total}
                    </span>
                    <span style={{ fontSize: 14, color: "#6B7280", fontWeight: 500 }}>
                        {Math.round(progressPct)}%
                    </span>
                </div>
                <div style={{ background: "#F3F4F6", borderRadius: 99, height: 6, overflow: "hidden" }}>
                    <div style={{ width: `${progressPct}%`, height: "100%", background: "#2563EB", borderRadius: 99, transition: "width 0.3s ease" }} />
                </div>
            </div>

            <div style={{
                border: "1px solid #E5E7EB", borderRadius: 20,
                padding: isMobile ? "28px 20px" : "40px 48px",
                boxShadow: "0 10px 30px rgba(17,24,39,0.05)",
                marginBottom: 20,
            }}>
                <p style={{ fontSize: isMobile ? 17 : 21, fontWeight: 700, color: "#111827", lineHeight: 1.55, margin: "0 0 28px" }}>
                    {question.question}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {OPTION_KEYS.map((key, i) => {
                        if (!question[key]) return null
                        const isSelected = selected === key
                        return (
                            <button
                                key={key}
                                onClick={() => setSelected(key)}
                                style={{
                                    display: "flex", alignItems: "center", gap: 14,
                                    padding: isMobile ? "13px 14px" : "15px 18px",
                                    borderRadius: 12, cursor: "pointer", textAlign: "left",
                                    border: isSelected ? "2px solid #2563EB" : "1.5px solid #E5E7EB",
                                    background: isSelected ? "#EFF6FF" : "white",
                                    fontFamily: "inherit",
                                    transition: "border-color 0.15s, background 0.15s",
                                }}
                            >
                                <span style={{
                                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 12, fontWeight: 700,
                                    background: isSelected ? "#2563EB" : "#F3F4F6",
                                    color: isSelected ? "white" : "#6B7280",
                                    transition: "background 0.15s, color 0.15s",
                                }}>
                                    {OPTION_LABELS[i]}
                                </span>
                                <span style={{ fontSize: isMobile ? 14 : 15, color: "#111827", lineHeight: 1.5 }}>
                                    {question[key]}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button
                    onClick={handlePrev}
                    disabled={currentIndex === 0}
                    style={{
                        background: "white",
                        color: currentIndex === 0 ? "#D1D5DB" : "#6B7280",
                        padding: "14px 24px", borderRadius: 12,
                        border: `1.5px solid ${currentIndex === 0 ? "#F3F4F6" : "#E5E7EB"}`,
                        cursor: currentIndex === 0 ? "not-allowed" : "pointer",
                        fontWeight: 600, fontSize: 16, fontFamily: "inherit",
                        transition: "color 0.15s, border-color 0.15s",
                    }}
                >
                    ← Previous
                </button>

                <button
                    onClick={handleNext}
                    disabled={!selected}
                    style={{
                        background: selected ? "#2563EB" : "#E5E7EB",
                        color: selected ? "white" : "#9CA3AF",
                        padding: "14px 28px", borderRadius: 12,
                        border: "none", cursor: selected ? "pointer" : "not-allowed",
                        fontWeight: 700, fontSize: 16, fontFamily: "inherit",
                        transition: "background 0.15s, color 0.15s",
                    }}
                >
                    {isLast ? "Submit →" : "Next →"}
                </button>
            </div>
        </div>
    )
}
